import "./LienStatusTrafficLight.scss";

import { useEffect, useRef, useState } from "react";

type LienStatus = {
  analysis_complete: boolean;
  client_submission: boolean;
  case_closed: boolean;
};

type LienStatusTrafficLightProps = {
  currentStatus: LienStatus;
  onStatusChange: (
    field: "analysis_complete" | "client_submission" | "case_closed",
    value: boolean,
  ) => void;
  onDelete: () => void;
};

const LienStatusTrafficLight = ({
  currentStatus,
  onStatusChange,
  onDelete,
}: LienStatusTrafficLightProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    right: 0,
  });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // close on ESC key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      // calculate position when opening
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownHeight = 240; // approximate height of dropdown
      const viewportHeight = window.innerHeight;

      // calculate top position, ensuring dropdown doesn't go off bottom
      let top = rect.top;
      if (top + dropdownHeight > viewportHeight) {
        // position above if not enough space below
        top = Math.max(10, viewportHeight - dropdownHeight - 10);
      }

      setDropdownPosition({
        top,
        right: window.innerWidth - rect.left + 8, // 8px gap from button
      });
    }
    setIsOpen(!isOpen);
  };

  const handleStatusToggle = (
    field: "analysis_complete" | "client_submission" | "case_closed",
  ) => {
    onStatusChange(field, !currentStatus[field]);
  };

  const handleDeleteClick = () => {
    setIsOpen(false);
    onDelete();
  };

  return (
    <div className="traffic-light-container">
      <button
        ref={buttonRef}
        className="traffic-light"
        onClick={handleToggle}
        aria-label="Lien Status"
        aria-expanded={isOpen}
      >
        <div
          className={`light ${currentStatus.analysis_complete ? "active" : ""}`}
          aria-label={`Analysis ${currentStatus.analysis_complete ? "complete" : "incomplete"}`}
        />
        <div
          className={`light ${currentStatus.client_submission ? "active" : ""}`}
          aria-label={`Client submission ${currentStatus.client_submission ? "complete" : "incomplete"}`}
        />
        <div
          className={`light ${currentStatus.case_closed ? "active" : ""}`}
          aria-label={`Case ${currentStatus.case_closed ? "closed" : "open"}`}
        />
      </button>

      {isOpen && (
        <div
          ref={dropdownRef}
          className="traffic-light-dropdown"
          style={{
            top: `${dropdownPosition.top}px`,
            right: `${dropdownPosition.right}px`,
          }}
        >
          <button
            className="status-option"
            onClick={() => handleStatusToggle("analysis_complete")}
          >
            <span
              className={`status-icon material-icons ${currentStatus.analysis_complete ? "completed" : ""}`}
            >
              analytics
            </span>
            <span className="status-label">Analysis Complete</span>
          </button>

          <button
            className="status-option"
            onClick={() => handleStatusToggle("client_submission")}
          >
            <span
              className={`status-icon material-icons ${currentStatus.client_submission ? "completed" : ""}`}
            >
              send
            </span>
            <span className="status-label">Client Submission</span>
          </button>

          <button
            className="status-option"
            onClick={() => handleStatusToggle("case_closed")}
          >
            <span
              className={`status-icon material-icons ${currentStatus.case_closed ? "completed" : ""}`}
            >
              {currentStatus.case_closed ? "circle" : "incomplete_circle"}
            </span>
            <span className="status-label">Case Closed</span>
          </button>

          <div className="dropdown-divider" />

          <button
            className="status-option delete-option"
            onClick={handleDeleteClick}
          >
            <span className="status-icon material-icons">delete</span>
            <span className="status-label">Delete Lien</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default LienStatusTrafficLight;
